import React, { useMemo, useCallback, useState } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import {
  Button,
  Segment,
  Bubble,
} from 'tea-component';
import {
  DEFAULT_NAME,
  DEFAULT_NAME_CN,
  DEFAULT_NAME_EN,
  MAX_FILTERS,
} from './constant';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLanguage } from '@home/utils/common';
import { IFilterPanelProps } from './type';
import {
  useFilterGroup,
  useFilterValidation,
  useFilterRenderers,
} from './hooks';
import FilterNameEdit from '@/pages/datalab/components/UserFilter/FilterNameEdit';
import FilterLogic from '@/pages/datalab/components/UserFilter/FilterLogic';
import ConfirmAndCancel from '@/pages/datalab/components/ConfirmAndCancle';
import SvgIcon from '@home/components/SvgIcon';
import './index.scss';

const FilterPanel: React.FC<IFilterPanelProps> = ({
  value,
  fieldOption = [],
  stringDataMap = {},
  stringLoading = false,
  defaultName = DEFAULT_NAME,
  defaultNameEn = DEFAULT_NAME_EN,
  defaultNameCn = DEFAULT_NAME_CN,
  max = MAX_FILTERS,
  header,
  isFavorite = false,
  getStringData,
  onChange,
  onConfirm,
  onCancel,
}) => {
  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [isConfirm, setIsConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<'current' | 'favorite'>('current');
  // 使用 hooks 管理状态和逻辑
  const {
    currentValue,
    fallbackName,
    createEmptyFilter,
    updateGroup,
    handleFilterChange,
    handleAddFilter,
    handleRemoveFilter,
    handleClear,
    handleLogicChange,
  } = useFilterGroup({
    value,
    defaultName,
    defaultNameEn,
    defaultNameCn,
    max,
    setIsConfirm,
    onChange,
  });
  const {
    id,
    name,
    name_en: nameEn,
    name_cn: nameCn,
    filters,
    logic,
  } = currentValue;

  const { isAllUsers, canAddFilter, canConfirm, canClear } = useFilterValidation({
    filters,
    name,
    max,
  });

  const { renderFilterItem } = useFilterRenderers({
    isConfirm,
    filters,
    fieldOption,
    stringDataMap,
    stringLoading,
    handleFilterChange,
    handleRemoveFilter,
    getStringData,
  });

  // 处理名称重命名
  const handleRenameTab = useCallback(
    ({ label }: { id: string; label: string; }) => {
      updateGroup({
        name: label,
        isNameCustomized: true,
      });
    },
    [updateGroup]
  );

  // 名称验证函数（检查是否重复）
  const checkName = useCallback(
    (newName: string) => {
      // 如果名称没有变化，允许
      if (newName === name) {
        return true;
      }
      // 这里可以添加其他验证逻辑，比如检查是否与其他组重名
      if (!newName?.trim()) {
        return false;
      }
      return true;
    },
    [name]
  );

  // 计算气泡提示内容
  const bubbleContent = useMemo(() => {
    if (!canAddFilter) {
      return filters.length >= max && canConfirm
        ? formatMessage({ id: 'dbl_filter_max_filters_reached' })
        : formatMessage({ id: 'dbl_filter_complete_current_filter' });
    }
    return null;
  }, [canAddFilter, filters.length, max, canConfirm, formatMessage]);

  // Segment切换
  const handleSegmentChange = useCallback((value: string | number) => {
    const tabId = value as 'current' | 'favorite';
    setActiveTab(tabId);
  }, []);

  // 确认
  const handleConfirm = useCallback(() => {
    setIsConfirm(true);
    // 验证所有条件是否完整
    if (!canConfirm) return;
    onConfirm?.(currentValue);
  }, [canConfirm, currentValue, onConfirm]);

  // 取消
  const handleCancel = useCallback(() => {
    if (value) {
      // 如果外部 value 的 filters 为空，确保至少有一组（3个）空的筛选器
      const resetValue = {
        ...value,
        filters: value?.filters?.length ? [...(value?.filters || [])] : [createEmptyFilter()],
      };
      updateGroup(resetValue);
    } else {
      updateGroup({
        filters: [createEmptyFilter()],
        name: fallbackName,
        name_en: defaultNameEn,
        name_cn: defaultNameCn,
        isNameCustomized: false,
        summaryEn: defaultNameEn,
        summaryCn: defaultNameCn,
        logic: 'AND',
      });
    }
    onCancel?.();
  }, [value, onCancel, createEmptyFilter, updateGroup, fallbackName, defaultNameEn, defaultNameCn]);

  // 收藏（占位功能）
  const handleFavorite = useCallback(() => {
    // TODO: 功能即将上线
  }, []);

  return (<div className="dbl-filter-panel">
    {/* Header */}
    {header && <div className="dbl-filter-panel-header">{header}</div>}
    {/* Segment Header */}
    <div className="dbl-filter-panel-segment">
      <Segment
        value={activeTab}
        onChange={handleSegmentChange}
        options={[
          {
            text: formatMessage({ id: 'dbl_filter_tab_current_filter' }),
            value: 'current',
          },
          {
            text: formatMessage({ id: 'dbl_filter_tab_my_favorite' }),
            value: 'favorite',
          },
        ]}
      />
    </div>
    {/* Content */}
    {activeTab === 'current' && (
      <div className="dbl-filter-panel-content">
        {/* Group Header */}
        <div className="dbl-filter-panel-name">
          <FilterNameEdit
            type="textarea"
            label={getLanguage(isEn, nameEn, nameCn) || name}
            id={id}
            onRenameTab={handleRenameTab}
            checkName={checkName}
          />
        </div>
        {/* Filter Body */}
        <div className={classNames('dbl-filter-panel-body', {
          'dbl-filter-panel-body-one': filters?.length === 1,
          'dbl-filter-panel-body-all-users': isAllUsers
        })}>
          {filters?.length > 1 && (
            <FilterLogic
              value={logic}
              isEn={isEn}
              onChange={(value) => handleLogicChange(value as 'AND' | 'OR')}
            />
          )}
          {/* Filter List */}
          <div className="dbl-filter-panel-list">
            {filters && filters?.length > 0 ? filters?.map((filter) => renderFilterItem(filter)) : null}
            {/* Add Filter Button */}
            <div className="dbl-filter-panel-actions">
              <Bubble content={bubbleContent}>
                <div>
                  <Button
                    type="link"
                    className='dbl-filter-panel-actions-button'
                    disabled={!canAddFilter}
                    onClick={handleAddFilter}
                  >
                    <SvgIcon type={'icon_add_game'} />
                    {formatMessage({ id: 'dbl_filter_add_new_filter' })}
                  </Button>
                </div>
              </Bubble>
              <Button
                type="link"
                className='dbl-filter-panel-actions-button'
                disabled={!canClear}
                onClick={handleClear}
              >
                {formatMessage({ id: 'dbl_filter_clear' })}
              </Button>
            </div>
          </div>
        </div>
        {/* Footer Actions */}
        <ConfirmAndCancel
          className="dbl-filter-panel-footer"
          bottomPadding={0}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          preNode={
            <Button
              className='dbl-btn-favorite'
              type="weak"
              onClick={handleFavorite}
            >
              <SvgIcon className='dbl-btn-favorite-icon' type={isFavorite ? 'star-action' : 'star-m'} />
              {formatMessage({ id: 'dbl_filter_favorite' })}
            </Button>
          }
        />
      </div>
    )}
    {activeTab === 'favorite' && (
      <div className="dbl-filter-panel-content">
        <div className="dbl-filter-panel-empty">
          {formatMessage({ id: 'common_event_selector_nodata' })}
        </div>
      </div>
    )}
  </div>);
};

export default FilterPanel;

